/**
 * Visual Aid Type Definitions
 * Centralized types for all visual components
 */

export type VisualAidType =
  | 'stairs'
  | 'fraction'
  | 'geo'
  | 'rule'
  | 'numberline'
  | 'ratio'
  | 'money'
  | 'column'
  | 'clock'
  | 'digitalClock'
  | 'decimalgrid'
  | 'verbTable'
  | 'kofschipMnemonic'
  | 'syllableBlocks'
  | 'circle'
  | 'coordinateGrid'
  | 'box3d'
  | 'compositeShape'
  | 'geometricShape'
  | 'readingPassage'
  | 'barChart'
  | 'lineChart'
  | 'calendar'
  | 'thermometer'
  | 'ruler'
  | 'measuringCup'
  | 'weighScale'
  | 'equalSharing'

export const VISUAL_AID_TYPES = [
  'stairs',
  'fraction',
  'geo',
  'rule',
  'numberline',
  'ratio',
  'money',
  'column',
  'clock',
  'digitalClock',
  'decimalgrid',
  'verbTable',
  'kofschipMnemonic',
  'syllableBlocks',
  'circle',
  'coordinateGrid',
  'box3d',
  'compositeShape',
  'geometricShape',
  'readingPassage',
  'barChart',
  'lineChart',
  'calendar',
  'thermometer',
  'ruler',
  'measuringCup',
  'weighScale',
  'equalSharing',
] as const satisfies readonly VisualAidType[]
export interface VisualAid {
  type: VisualAidType
  data: Record<string, unknown>
  showWhen?: 'always' | 'wrong' | 'correct'
  /** Indicator value for answer-parity check (PEDAGOGICAL_CONTRACT_SPEC §4) */
  indicatorValue?: string | number
}

// Money Visual Data
export interface MoneyVisualData {
  amount: number
  breakdown?: MoneyBreakdown
  showCoins?: boolean
  showBills?: boolean
  interactive?: boolean
  label?: string
}
export interface MoneyBreakdown {
  bills: {
    value: number
    count: number
  }[]
  coins: {
    value: number
    count: number
  }[]
}

// Column Visual Data (for arithmetic)
export interface ColumnVisualData {
  operation: 'add' | 'subtract' | 'multiply' | 'divide'
  operand1: number
  operand2: number
  showSteps?: boolean
  currentStep?: number
  animated?: boolean
}

// Verb Table Visual Data (for SWW module)
export interface VerbTableData {
  verb: string
  tense: 'present' | 'past' | 'perfect'
  persons: string[]
  forms: string[]
  highlight?: string
  label?: string
}

// Kofschip Mnemonic Visual Data (for SWW module)
export interface KofschipMnemonicData {
  verb: string
  stem: string
  lastLetter: string
  useTE: boolean
  label?: string
}

// Syllable Blocks Visual Data (for SPR module)
export interface SyllableBlocksData {
  word: string
  syllables: string[]
  types: ('open' | 'closed')[]
  highlight?: number
  label?: string
}
